import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSubjectArea,
  deleteSubjectArea,
  listSubjectAreas,
  reorderSubjectAreas,
  updateSubjectArea,
} from "./subject-areas-api";
import { SCHEDULE_EVENTS_BASE_KEY } from "./use-schedules";
import type {
  SubjectAreaCreateRequest,
  SubjectAreaUpdateRequest,
} from "../types";

export const SUBJECT_AREAS_KEY = (scheduleId: string) =>
  ["schedules", scheduleId, "subject-areas"] as const;

export function useSubjectAreas(
  scheduleId: string,
  options: { enabled?: boolean } = {},
) {
  const enabledGate = options.enabled ?? true;
  return useQuery({
    queryKey: SUBJECT_AREAS_KEY(scheduleId),
    queryFn: () => listSubjectAreas(scheduleId),
    enabled: enabledGate && !!scheduleId,
  });
}

export function useCreateSubjectArea(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubjectAreaCreateRequest) =>
      createSubjectArea(scheduleId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUBJECT_AREAS_KEY(scheduleId) });
    },
  });
}

export function useUpdateSubjectArea(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      areaId,
      payload,
    }: {
      areaId: string;
      payload: SubjectAreaUpdateRequest;
    }) => updateSubjectArea(scheduleId, areaId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUBJECT_AREAS_KEY(scheduleId) });
      // Calendar event coloring depends on subject-area ColorHex; invalidate
      // the events cache so the grid re-renders when a color changes. The
      // calendar grid actually reads the color in Slice 5, but the hook lives
      // here today so the contract is in one place.
      qc.invalidateQueries({
        queryKey: SCHEDULE_EVENTS_BASE_KEY(scheduleId),
      });
    },
  });
}

export function useDeleteSubjectArea(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (areaId: string) => deleteSubjectArea(scheduleId, areaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUBJECT_AREAS_KEY(scheduleId) });
    },
  });
}

export function useReorderSubjectAreas(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      reorderSubjectAreas(scheduleId, orderedIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUBJECT_AREAS_KEY(scheduleId) });
    },
  });
}
